import {
  ColumnDef,
  OnChangeFn,
  PaginationState,
  RowData,
  SortingState,
  TableOptions,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import c from "classnames";
import { useMemo } from "react";

import { Box } from "@design-system/Box/Box";
import { Clickable } from "@design-system/Clickable/Clickable";
import { Icon } from "@design-system/Icon/Icon";
import { Skeleton } from "@design-system/Skeleton/Skeleton";
import { Text } from "@design-system/Text/Text";

import { CompanyCell } from "./UI/CompanyCell/CompanyCell";
import { DateCell } from "./UI/DateCell/DateCell";
import { Pagination } from "./UI/Pagination/Pagination";
import { UserCell } from "./UI/UserCell/UserCell";

import classes from "./Table.module.css";

type Props<T, TValue = unknown> = {
  columns: ColumnDef<T, TValue>[];
  data: T[];
  rowCount?: number;
  pagination: PaginationState;
  onPaginationChange: TableOptions<T>["onPaginationChange"];
  isLoading?: boolean;
  isFetching?: boolean;
  emptyPlaceholder?: string;
  onPageSizeChange: (pageSize: number) => void;
  columnVisibility?: Record<string, boolean>;
  sorting: SortingState;
  showPageSizeDropdown?: boolean;
  onSortingChange: OnChangeFn<SortingState>;
  showHeader?: boolean;
  showFooter?: boolean;
  getRowClassName?: (row: T) => string | undefined;
  showCheckboxColumn?: boolean;
  selectedRowId?: string;
  onRowSelect?: (rowId: string | undefined) => void;
  isRowSelectable?: (row: T) => boolean;
  manualSorting?: boolean;
};

export const Table = <T, TValue = unknown>({
  columns,
  data,
  rowCount,
  pagination,
  onPaginationChange,
  isLoading,
  isFetching,
  onPageSizeChange,
  columnVisibility,
  sorting,
  onSortingChange,
  emptyPlaceholder,
  showPageSizeDropdown = true,
  showHeader = true,
  showFooter = true,
  getRowClassName,
  showCheckboxColumn = false,
  selectedRowId,
  onRowSelect,
  isRowSelectable,
  manualSorting = false,
}: Props<T, TValue>) => {
  // Create checkbox column if needed
  const allColumns = useMemo(() => {
    if (!showCheckboxColumn) {
      return columns;
    }
    const checkboxColumn: ColumnDef<T, unknown> = {
      id: "select",
      header: "",
      cell: ({ row }) => {
        const rowData = row.original as Record<string, unknown>;
        const rowId = rowData.id;
        const isSelectable = isRowSelectable
          ? isRowSelectable(row.original)
          : true;
        const isSelected = selectedRowId === rowId;

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "40px",
            }}
          >
            <input
              type="checkbox"
              checked={isSelected}
              disabled={!isSelectable}
              onChange={() => {
                if (isSelectable && onRowSelect) {
                  onRowSelect(isSelected ? undefined : String(rowId));
                }
              }}
              style={{ cursor: isSelectable ? "pointer" : "not-allowed" }}
            />
          </div>
        );
      },
      size: 40,
      minSize: 40,
      maxSize: 40,
      enableSorting: false,
      enableResizing: false,
      meta: {
        fitContent: true,
        checkboxColumn: true,
      },
    };

    return [checkboxColumn, ...columns];
  }, [
    columns,
    showCheckboxColumn,
    selectedRowId,
    onRowSelect,
    isRowSelectable,
  ]);

  const table = useReactTable<T>({
    data: isLoading
      ? Array.from({ length: pagination.pageSize }, () => ({ id: "1" }) as T)
      : data,
    columns: allColumns,
    manualPagination: true,
    rowCount,
    onPaginationChange,
    state: {
      pagination,
      columnVisibility,
      sorting,
    },
    onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? getCoreRowModel() : getSortedRowModel(),
  });

  return (
    <>
      <div className={classes.container}>
        {table.getRowCount() > 0 && (
          <table className={classes.table}>
            {showHeader && (
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={c(
                          header.column.columnDef.meta?.right && classes.right,
                          header.column.columnDef.meta?.fitContent &&
                            classes.fitContent,
                          header.column.columnDef.meta?.sticky &&
                            classes.sticky,
                          header.column.columnDef.meta?.checkboxColumn &&
                            classes.checkboxColumn,
                        )}
                      >
                        {header.isPlaceholder ||
                        !header.column.columnDef
                          .header ? null : header.column.getCanSort() ? (
                          <Clickable
                            outline="sm"
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            <Box gap={4} align="center">
                              <Text
                                size={11}
                                weight={600}
                                color="textSecondary"
                                uppercase
                                truncate
                                maxWidth={250}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                              </Text>
                              <Icon
                                size="2xs"
                                icon={
                                  header.column.getIsSorted() === "asc"
                                    ? "faSortDown"
                                    : header.column.getIsSorted() === "desc"
                                      ? "faSortUp"
                                      : "faSort"
                                }
                              />
                            </Box>
                          </Clickable>
                        ) : (
                          <Box gap={4} align="center">
                            <Text
                              size={11}
                              weight={600}
                              color="textSecondary"
                              uppercase
                              truncate
                              maxWidth={250}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </Text>
                          </Box>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
            )}
            <tbody>
              {isFetching && (
                <tr className={classes.activityRow}>
                  <td colSpan={allColumns.length}>
                    <div className={classes.activity}>
                      <Skeleton
                        baseColor="transparent"
                        width="100%"
                        height={2}
                      />
                    </div>
                  </td>
                </tr>
              )}
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={
                    getRowClassName ? getRowClassName(row.original) : undefined
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={c(
                        cell.column.columnDef.meta?.right && classes.right,
                        cell.column.columnDef.meta?.fitContent &&
                          classes.fitContent,
                        cell.column.columnDef.meta?.sticky && classes.sticky,
                        cell.column.columnDef.meta?.borderLeft &&
                          classes.borderLeft,
                        cell.column.columnDef.meta?.checkboxColumn &&
                          classes.checkboxColumn,
                      )}
                    >
                      {isLoading ? (
                        <Skeleton width="100%" height={20} />
                      ) : (cell.getValue() !== "" &&
                          cell.getValue() !== null &&
                          cell.getValue() !== undefined) ||
                        cell.column.columnDef.meta?.actions ||
                        cell.column.columnDef.meta?.checkboxColumn ? (
                        flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )
                      ) : (
                        <Text size={12} weight={400} color="textSecondary">
                          -
                        </Text>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {table.getRowCount() === 0 && (
          <Box
            gap={16}
            direction="column"
            justify="center"
            align="center"
            fullStretch
          >
            <div className={classes.empty}>
              <Text size={64} weight={400} color="textSecondary">
                <Icon icon="faFileInvoice" />
              </Text>
              <Text size={16} weight={400} color="textSecondary">
                {emptyPlaceholder ?? "No matching results found..."}
              </Text>
            </div>
          </Box>
        )}
      </div>

      {showFooter && (
        <div className={classes.pagination}>
          <Pagination
            pageCount={table.getPageCount()}
            rowCount={table.getRowCount()}
            currentPage={table.getState().pagination.pageIndex}
            onPageClick={table.setPageIndex}
            onNextClick={table.nextPage}
            onPreviousClick={table.previousPage}
            pageSize={table.getState().pagination.pageSize}
            currentPageSize={data.length}
            canNextPage={table.getCanNextPage()}
            canPreviousPage={table.getCanPreviousPage()}
            pageOptions={table.getPageOptions()}
            onPageSizeChange={onPageSizeChange}
            showPageSizeDropdown={showPageSizeDropdown}
          />
        </div>
      )}
    </>
  );
};

Table.DateCell = DateCell;
Table.CompanyCell = CompanyCell;
Table.UserCell = UserCell;

// Extends the ColumnMeta type to include meta properties
declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    right?: boolean;
    fitContent?: boolean;
    sticky?: boolean;
    borderLeft?: boolean;
    actions?: boolean;
    checkboxColumn?: boolean;
  }
}
